import { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Icon } from '@iconify/react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { TabsLayout } from '@/layouts/TabsLayout'
import type { TabNavigationItem } from '@/components/Tabs/TabsNavigation'
import { CampaignTotpStepUpProvider, useCampaignTotpStepUp } from '@/contexts/CampaignTotpStepUpContext'
import { RaspadinhaCampaignsProvider, useRaspadinhaCampaigns } from '@/contexts/RaspadinhaCampaignsContext'
import { canManageInstitutionCampaigns } from '@/lib/campaign-totp-step-up'
import {
  isValidRaspadinhaCampaignSlug,
  normalizeRaspadinhaCampaignSlug,
} from '@/lib/raspadinha-campaigns'
import {
  CampanhaRaspadinhaLayoutProvider,
  useCampanhaRaspadinhaLayout,
} from './CampanhaRaspadinhaLayoutContext'
import layoutStyles from '../CampanhasLayout/CampanhasLayout.module.css'
import raspadinhaLayoutStyles from './CampanhaRaspadinhaLayout.module.css'

const TAB_LISTAGEM: TabNavigationItem = {
  value: 'listagem',
  label: 'Campanhas Raspadinha',
  icon: 'mdi:ticket-confirmation-outline',
  to: '/campanha-raspadinha',
}

const TAB_EDITAR: TabNavigationItem = {
  value: 'editar',
  label: 'Editar',
  icon: 'mdi:pencil',
  to: '/campanha-raspadinha/editar',
}

const TAB_DASHBOARD: TabNavigationItem = {
  value: 'dashboard',
  label: 'Dashboard',
  icon: 'mdi:chart-line',
  to: '/campanha-raspadinha/dashboard',
}

const RASPADINHA_EDITAR_PATH = '/campanha-raspadinha/editar'
const RASPADINHA_DASHBOARD_PATH = '/campanha-raspadinha/dashboard'
const CREATE_MODAL_QUERY_KEY = 'modal'
const CREATE_MODAL_QUERY_VALUE = 'create'

type CreateCampaignModalProps = {
  errorMessage: string | null
  isSubmitting: boolean
  name: string
  onClose: () => void
  onNameChange: (value: string) => void
  onSlugChange: (value: string) => void
  onSubmit: (event: FormEvent<HTMLFormElement>) => void
  slug: string
}

function CreateRaspadinhaCampaignModal({
  errorMessage,
  isSubmitting,
  name,
  onClose,
  onNameChange,
  onSlugChange,
  onSubmit,
  slug,
}: CreateCampaignModalProps) {
  return (
    <div
      className={layoutStyles.modalOverlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-raspadinha-modal-title"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose()
        }
      }}
    >
      <div className={layoutStyles.modalCard}>
        <div className={layoutStyles.modalHeader}>
          <div>
            <h2 id="create-raspadinha-modal-title" className={layoutStyles.modalTitle}>
              Criar campanha raspadinha
            </h2>
            <p className={layoutStyles.modalSubtitle}>
              Defina o nome e o slug inicial. Depois disso, a edição continua na campanha criada.
            </p>
          </div>
          <button
            type="button"
            className={layoutStyles.modalClose}
            onClick={onClose}
            aria-label="Fechar modal de criação"
            disabled={isSubmitting}
          >
            <Icon icon="mdi:close" aria-hidden />
          </button>
        </div>

        <form className={layoutStyles.modalForm} onSubmit={onSubmit}>
          <div className={layoutStyles.modalBody}>
            <div className={layoutStyles.fieldGroup}>
              <label className={layoutStyles.fieldLabel} htmlFor="create-raspadinha-name">
                Nome da campanha raspadinha
              </label>
              <input
                id="create-raspadinha-name"
                className={layoutStyles.fieldInput}
                type="text"
                value={name}
                onChange={(event) => onNameChange(event.target.value)}
                placeholder="Ex: Raspadinha de Natal 2026"
                autoFocus
                disabled={isSubmitting}
                required
              />
            </div>

            <div className={layoutStyles.fieldGroup}>
              <label className={layoutStyles.fieldLabel} htmlFor="create-raspadinha-slug">
                Slug
              </label>
              <input
                id="create-raspadinha-slug"
                className={layoutStyles.fieldInput}
                type="text"
                value={slug}
                onChange={(event) => onSlugChange(event.target.value)}
                placeholder="raspadinha-de-natal-2026"
                disabled={isSubmitting}
                required
              />
              <p className={layoutStyles.fieldHint}>
                URL pública: <strong>/raspadinha/{slug || 'slug-da-campanha'}</strong>
              </p>
              <p className={layoutStyles.fieldHint}>
                Use apenas letras minúsculas, números e hífens.
              </p>
            </div>

            {errorMessage ? (
              <div className={layoutStyles.modalError} role="alert">
                {errorMessage}
              </div>
            ) : null}
          </div>

          <div className={layoutStyles.modalFooter}>
            <button
              type="button"
              className={layoutStyles.modalSecondaryButton}
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className={layoutStyles.modalPrimaryButton}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Criando...' : 'Criar campanha raspadinha'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function CampanhaRaspadinhaLayoutInner() {
  const location = useLocation()
  const navigate = useNavigate()
  const { guard, isCollaborator } = useAuth()
  const { ensureElevated, runWithStepUp } = useCampaignTotpStepUp()
  const { createCampaign } = useRaspadinhaCampaigns()
  const { saveTrigger, isSubmitting, hasUnsavedChanges, isSaveSuccessVisible } =
    useCampanhaRaspadinhaLayout()
  const canManage = canManageInstitutionCampaigns(guard, Boolean(isCollaborator))
  const openingCreateRef = useRef(false)
  const [isSavingClick, setIsSavingClick] = useState(false)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [createName, setCreateName] = useState('')
  const [createSlug, setCreateSlug] = useState('')
  const [hasEditedSlug, setHasEditedSlug] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  const [isCreatingCampaign, setIsCreatingCampaign] = useState(false)
  const isCreatingCampaignRef = useRef(false)
  const showingSave = isSavingClick || isSubmitting

  const resetCreateModalState = useCallback(() => {
    setCreateName('')
    setCreateSlug('')
    setHasEditedSlug(false)
    setCreateError(null)
  }, [])

  const clearCreateModalQuery = useCallback(() => {
    const params = new URLSearchParams(location.search)
    if (params.get(CREATE_MODAL_QUERY_KEY) !== CREATE_MODAL_QUERY_VALUE) return

    params.delete(CREATE_MODAL_QUERY_KEY)
    const nextSearch = params.toString()
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
      },
      { replace: true }
    )
  }, [location.pathname, location.search, navigate])

  const openCreateModal = useCallback(() => {
    if (!canManage || openingCreateRef.current) return
    openingCreateRef.current = true
    void ensureElevated().then((allowed) => {
      openingCreateRef.current = false
      if (!allowed) return
      resetCreateModalState()
      setIsCreateModalOpen(true)
    })
  }, [canManage, ensureElevated, resetCreateModalState])

  const closeCreateModal = useCallback(() => {
    if (isCreatingCampaignRef.current) return
    setIsCreateModalOpen(false)
    resetCreateModalState()
    clearCreateModalQuery()
  }, [clearCreateModalQuery, resetCreateModalState])

  useEffect(() => {
    if (!isSubmitting) {
      const sync = window.setTimeout(() => {
        setIsSavingClick(false)
      }, 0)
      return () => window.clearTimeout(sync)
    }
  }, [isSubmitting])

  useEffect(() => {
    if (!isSavingClick) return
    const fallback = setTimeout(() => {
      setIsSavingClick((prev) => (isSubmitting ? prev : false))
    }, 800)
    return () => clearTimeout(fallback)
  }, [isSavingClick, isSubmitting])

  useEffect(() => {
    if (!canManage) return

    const params = new URLSearchParams(location.search)
    const shouldOpenModal = params.get(CREATE_MODAL_QUERY_KEY) === CREATE_MODAL_QUERY_VALUE

    if (shouldOpenModal && !isCreateModalOpen && !openingCreateRef.current) {
      openingCreateRef.current = true
      void ensureElevated().then((allowed) => {
        openingCreateRef.current = false
        if (!allowed) {
          clearCreateModalQuery()
          return
        }
        resetCreateModalState()
        setIsCreateModalOpen(true)
      })
    }
  }, [
    canManage,
    clearCreateModalQuery,
    ensureElevated,
    isCreateModalOpen,
    location.search,
    resetCreateModalState,
  ])

  const handleSaveClick = useCallback(() => {
    if (isSavingClick || isSubmitting) return
    setIsSavingClick(true)
    if (saveTrigger) {
      saveTrigger()
      return
    }
    const form = document.getElementById('raspadinha-edit-form') as HTMLFormElement | null
    form?.requestSubmit()
  }, [isSavingClick, isSubmitting, saveTrigger])

  const handleCreateNameChange = useCallback(
    (value: string) => {
      setCreateName(value)
      setCreateError(null)
      if (!hasEditedSlug) {
        setCreateSlug(normalizeRaspadinhaCampaignSlug(value))
      }
    },
    [hasEditedSlug]
  )

  const handleCreateSlugChange = useCallback((value: string) => {
    setHasEditedSlug(true)
    setCreateSlug(normalizeRaspadinhaCampaignSlug(value))
    setCreateError(null)
  }, [])

  const validateCreateModal = useCallback((): string | null => {
    if (!canManage) {
      return 'Apenas instituições podem criar campanhas raspadinha.'
    }

    if (!createName.trim()) {
      return 'Informe o nome da campanha raspadinha.'
    }

    if (!createSlug.trim()) {
      return 'Informe o slug da campanha raspadinha.'
    }

    if (!isValidRaspadinhaCampaignSlug(createSlug)) {
      return 'Use apenas letras minúsculas, números e hífens no slug.'
    }

    return null
  }, [canManage, createName, createSlug])

  const handleCreateCampaignSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()

      if (isCreatingCampaignRef.current) return

      const validationError = validateCreateModal()
      if (validationError) {
        setCreateError(validationError)
        return
      }

      isCreatingCampaignRef.current = true
      setIsCreatingCampaign(true)
      setCreateError(null)

      try {
        const campaign = await runWithStepUp(() =>
          createCampaign({
            name: createName.trim(),
            slug: createSlug.trim(),
          })
        )

        setIsCreateModalOpen(false)
        resetCreateModalState()
        navigate(
          {
            pathname: RASPADINHA_EDITAR_PATH,
            search: `?id=${campaign.id}`,
            hash: '#identidade',
          },
          { replace: true }
        )
      } catch (error) {
        setCreateError(
          error instanceof Error
            ? error.message
            : 'Não foi possível criar a campanha raspadinha.'
        )
      } finally {
        isCreatingCampaignRef.current = false
        setIsCreatingCampaign(false)
      }
    },
    [createCampaign, createName, createSlug, navigate, resetCreateModalState, runWithStepUp, validateCreateModal]
  )

  const tabs = useMemo(() => {
    const base: TabNavigationItem[] = [TAB_LISTAGEM]
    const isEditRoute = location.pathname === RASPADINHA_EDITAR_PATH
    const isDashboardRoute = location.pathname === RASPADINHA_DASHBOARD_PATH
    const dashboardId = new URLSearchParams(location.search).get('id')
    const hasSelectedCampaignForDashboard =
      dashboardId !== null && Number.isFinite(Number(dashboardId))

    if (isEditRoute) {
      base.push(TAB_EDITAR)
    }
    if (isDashboardRoute && hasSelectedCampaignForDashboard) {
      base.push({
        ...TAB_DASHBOARD,
        to: `/campanha-raspadinha/dashboard?id=${dashboardId}`,
      })
    }
    return base
  }, [location.pathname, location.search])

  const isEditRoute = location.pathname === RASPADINHA_EDITAR_PATH

  const saveButtonVisualState = showingSave
    ? 'saving'
    : isSaveSuccessVisible
      ? 'success'
      : hasUnsavedChanges
        ? 'dirty'
        : 'idle'

  const fixedSaveButton =
    canManage && isEditRoute && typeof document !== 'undefined'
      ? createPortal(
          <div className={raspadinhaLayoutStyles.fixedSaveBar}>
            <button
              type="button"
              className={`${raspadinhaLayoutStyles.fixedSaveButton} ${
                saveButtonVisualState === 'dirty'
                  ? raspadinhaLayoutStyles.fixedSaveButtonDirty
                  : saveButtonVisualState === 'success'
                    ? raspadinhaLayoutStyles.fixedSaveButtonSuccess
                    : raspadinhaLayoutStyles.fixedSaveButtonIdle
              }`}
              onClick={handleSaveClick}
              disabled={saveButtonVisualState !== 'dirty'}
              aria-busy={showingSave}
              aria-live="polite"
              title={
                saveButtonVisualState === 'dirty'
                  ? 'Salvar alterações da campanha raspadinha'
                  : saveButtonVisualState === 'success'
                    ? 'Campanha salva com sucesso'
                    : 'Nenhuma alteração pendente para salvar'
              }
            >
              {saveButtonVisualState === 'saving' ? (
                <>
                  <Icon icon="mdi:loading" data-spin aria-hidden />
                  Salvando...
                </>
              ) : saveButtonVisualState === 'success' ? (
                <>
                  <Icon
                    icon="mdi:check-circle-outline"
                    className={raspadinhaLayoutStyles.fixedSaveButtonSuccessIcon}
                    aria-hidden
                  />
                  Salvo com sucesso
                </>
              ) : (
                <>
                  <Icon icon="mdi:content-save-outline" aria-hidden />
                  Salvar campanha raspadinha
                </>
              )}
            </button>
          </div>,
          document.body
        )
      : null

  const headerAction = useMemo(() => {
    if (!canManage) return null
    if (isEditRoute) {
      return null
    }
    return (
      <button
        type="button"
        className={layoutStyles.createButton}
        onClick={openCreateModal}
        aria-haspopup="dialog"
      >
        <Icon icon="mdi:plus" aria-hidden />
        Criar Campanha Raspadinha
      </button>
    )
  }, [canManage, isEditRoute, openCreateModal])

  return (
    <>
      <TabsLayout
        title="Campanha Raspadinha"
        subtitle="Gerencie campanhas promocionais no formato raspadinha"
        tabs={tabs}
        headerAction={headerAction}
      />

      {fixedSaveButton}

      {isCreateModalOpen ? (
        <CreateRaspadinhaCampaignModal
          errorMessage={createError}
          isSubmitting={isCreatingCampaign}
          name={createName}
          onClose={closeCreateModal}
          onNameChange={handleCreateNameChange}
          onSlugChange={handleCreateSlugChange}
          onSubmit={(event) => {
            void handleCreateCampaignSubmit(event)
          }}
          slug={createSlug}
        />
      ) : null}
    </>
  )
}

export function CampanhaRaspadinhaLayout() {
  return (
    <RaspadinhaCampaignsProvider>
      <CampaignTotpStepUpProvider>
        <CampanhaRaspadinhaLayoutProvider>
          <CampanhaRaspadinhaLayoutInner />
        </CampanhaRaspadinhaLayoutProvider>
      </CampaignTotpStepUpProvider>
    </RaspadinhaCampaignsProvider>
  )
}
